'use client'

import { useEffect, useRef, useState } from 'react'
import { AlertCircle, Loader2 } from 'lucide-react'

/**
 * Scanner QR intégré (pas de dépendance) : utilise l'API BarcodeDetector
 * native (Android WebView / Chrome). Si elle n'existe pas, on prévient et on
 * laisse l'utilisateur saisir le code à la main (géré par le parent).
 *
 * onResult reçoit le texte brut du QR (souvent une URL .../app/join?room=CODE).
 */
export default function QrScanner({
  onResult,
  onUnsupported,
}: {
  onResult: (text: string) => void
  onUnsupported?: () => void
}) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const aliveRef = useRef(true)
  const firedRef = useRef(false)
  const [err, setErr] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    aliveRef.current = true
    const BD = (typeof window !== 'undefined') ? (window as any).BarcodeDetector : null
    if (!BD) { onUnsupported?.(); setErr('unsupported'); setLoading(false); return }

    let detector: any = null
    try { detector = new BD({ formats: ['qr_code'] }) }
    catch { onUnsupported?.(); setErr('unsupported'); setLoading(false); return }

    let timer: ReturnType<typeof setTimeout> | null = null

    const stop = () => {
      aliveRef.current = false
      if (timer) clearTimeout(timer)
      try { streamRef.current?.getTracks().forEach(t => t.stop()) } catch {}
    }

    const loop = async () => {
      if (!aliveRef.current || firedRef.current) return
      const v = videoRef.current
      if (v && v.readyState >= 2) {
        try {
          const codes = await detector.detect(v)
          if (codes && codes.length && codes[0].rawValue) {
            firedRef.current = true
            stop()
            onResult(String(codes[0].rawValue))
            return
          }
        } catch {}
      }
      timer = setTimeout(loop, 280)
    }

    const start = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } }, audio: false,
        })
        if (!aliveRef.current) { stream.getTracks().forEach(t => t.stop()); return }
        streamRef.current = stream
        const v = videoRef.current!
        v.srcObject = stream
        v.setAttribute('playsinline', 'true')
        await v.play().catch(() => {})
        setLoading(false)
        loop()
      } catch {
        setErr('camera')
        setLoading(false)
      }
    }
    start()
    return stop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (err === 'unsupported') {
    return (
      <div className="rounded-2xl border border-white/[0.07] bg-spanel p-4 text-sm text-white/65 flex items-start gap-2.5 text-left">
        <AlertCircle size={17} className="text-a4 shrink-0 mt-0.5" />
        <span>Le scan caméra n'est pas dispo sur cet appareil. Tape le code à la main juste en dessous.</span>
      </div>
    )
  }

  return (
    <div className="relative w-full aspect-square max-w-xs mx-auto rounded-3xl overflow-hidden bg-sbg ring-1 ring-white/10 shadow-[0_16px_50px_rgba(0,0,0,.5)]">
      <video ref={videoRef} muted playsInline className="w-full h-full object-cover" />
      {/* Cadre de visée */}
      <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
        <div className="w-2/3 h-2/3 rounded-2xl border-2 border-a3 shadow-[0_0_0_2000px_rgb(var(--sbg)/0.45),0_0_24px_rgb(var(--a3)/.55)]" />
      </div>
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center text-white/70 gap-2 text-sm">
          <Loader2 size={18} className="animate-spin text-a4" /> Ouverture de la caméra…
        </div>
      )}
      {err === 'camera' && (
        <div className="absolute inset-0 flex items-center justify-center p-4 text-center text-sm text-white/75 bg-sbg/80">
          Caméra inaccessible. Autorise l'accès, ou saisis le code à la main en dessous.
        </div>
      )}
    </div>
  )
}
